import { GroupPerformerTypeFilter, GroupPerformerTypes, RoleFilter } from '@shared/types';
import { CommercialType, RolePerformerType, RoleType } from '@talon/web/lib/api';
import { getMediaPerformerTypes, getRoleTypeFor } from '@talon/web/src/utils/activityRoles';
import { useUndoableDelete } from '@talon/web/src/utils/useUndoableDelete';
import { Box, Button, FormField, ResponsiveContext, Select, Text } from 'grommet';
import { Dispatch, SetStateAction, useContext, useEffect, useRef } from 'react';
import { LineItemDelete, TalonInput } from '../..';

interface IRolePerformerTabProps {
  setRolePerformers: Dispatch<SetStateAction<IRolePerformer[]>>;
  rolePerformers: IRolePerformer[];
  media: CommercialType[];
}
export interface IRolePerformer {
  name: string;
  roleType: RoleType;
  performerType: RolePerformerType;
  groupSize: number;
}
export const defaultRolePerformer: IRolePerformer = {
  name: '', roleType: RoleType.Actor, performerType: RolePerformerType.OnCameraPrincipal, groupSize: 1,
};
export const formatDefaultName = (performer: IRolePerformer, i?: number) => {
  const name = performer.name.trim() || `${performer.roleType} - ${performer.performerType}`;
  return performer.groupSize > 1 ? `${name} ${i ? `(${i} of ${performer.groupSize})` : `of ${performer.groupSize}`}` : name;
};

/** A role changed to another performer type: a role type that type allows, and a group's size for a group */
const withPerformerType = (performer: IRolePerformer, performerType: RolePerformerType): IRolePerformer => ({
  ...performer,
  performerType,
  roleType: getRoleTypeFor(performerType, performer.roleType) as RoleType,
  groupSize: GroupPerformerTypes.includes(performerType) ? Math.max(2, performer.groupSize) : 1,
});

export const RolePerformersTab = ({ rolePerformers, setRolePerformers, media }: IRolePerformerTabProps) => {
  const mobile = useContext(ResponsiveContext) === 'small';
  const deleteWithUndo = useUndoableDelete();
  const focusIndex = useRef<number | undefined>(undefined);
  // The performer types the project's media allow (a radio project's are radio, radio group and AFM)
  const allowed = getMediaPerformerTypes(media) as RolePerformerType[] | undefined;
  const performerKeys = (Object.keys(RolePerformerType) as Array<keyof typeof RolePerformerType>)
    .filter((key) => !GroupPerformerTypeFilter.includes(RolePerformerType[key]))
    .filter((key) => !allowed || allowed.includes(RolePerformerType[key]));
  // Roles of a type the media don't allow (the default on-camera principal, for a radio project) take the first allowed
  useEffect(() => {
    if (allowed && rolePerformers.some((performer) => !allowed.includes(performer.performerType)))
      setRolePerformers((current) => current.map((performer) =>
        allowed.includes(performer.performerType) ? performer : withPerformerType(performer, allowed[0])));
  }, [allowed, rolePerformers, setRolePerformers]);
  const update = (index: number, change: Partial<IRolePerformer>) =>
    setRolePerformers((current) => current.map((role, i) => i === index ? { ...role, ...change } : role));
  useEffect(() => {
    if (focusIndex.current !== undefined) {
      document.getElementById(`performer-${focusIndex.current}`)?.focus();
      focusIndex.current = undefined;
    }
  }, [rolePerformers.length]);

  return <Box pad="medium" gap="medium">
    {!mobile && <Box background="#F7F7F7" border={{ side: 'bottom', color: '#E3E3E3' }}
      style={{ display: 'grid', gridTemplateColumns: '1.3fr 1.3fr .9fr .8fr 40px', gap: 8, padding: '8px 14px' }}>
      {['Role name', 'Performer type', 'Role type', 'Group size', ''].map((label, i) =>
        <Text key={i} size="12px" color="#666">{label}</Text>)}
    </Box>}
    {rolePerformers.map((performer, index) => {
      const group = GroupPerformerTypes.includes(performer.performerType);
      const placeholder = group ? 'e.g. Dancers' : index === 0 ? 'e.g. Mom, Lead dancer' : 'e.g. Kid';
      return <Box key={index} className="line-item" border={{ side: mobile ? 'all' : 'bottom', color: '#EDEDED' }}
        round={mobile ? '8px' : undefined}
        style={mobile ? { display: 'grid', gridTemplateColumns: '1fr 40px', gap: 8, padding: 14 } :
          { display: 'grid', gridTemplateColumns: '1.3fr 1.3fr .9fr .8fr 40px', gap: 8, padding: '8px 14px' }}>
        <Box style={mobile ? { gridColumn: '1 / -1' } : undefined}>
          <TalonInput id={`performer-${index}`} name={`performer-${index}`} label={mobile ? 'Role name' : undefined}
            aria-label="Role name"
            placeholder={placeholder} val={performer.name} plain={false} saveDelay={0}
            onChange={(name) => update(index, { name })} />
        </Box>
        <FormField label={mobile ? 'Performer type' : undefined} htmlFor={`performer-type-${index}`}
          style={mobile ? { gridColumn: '1 / -1' } : undefined}>
          <Select id={`performer-type-${index}`} options={performerKeys.map((key) => RolePerformerType[key])}
            value={performer.performerType} onChange={({ option }) => update(index, withPerformerType(performer, option))}
            size="small" />
        </FormField>
        <FormField label={mobile ? 'Role type' : undefined} htmlFor={`role-type-${index}`}
          style={mobile ? { gridColumn: '1 / -1' } : undefined}>
          <Select id={`role-type-${index}`}
            options={(Object.keys(RoleType) as Array<keyof typeof RoleType>)
              .filter((key) => RoleFilter[performer.performerType]?.includes(RoleType[key]))
              .map((key) => RoleType[key])}
            value={performer.roleType} onChange={({ option }) => update(index, { roleType: option })} size="small" />
        </FormField>
        <Box gap="xsmall">
          {mobile && <Text size="12px" color="#666">Group size</Text>}
          {group ? <Box direction="row" align="center" justify="between"
            width={mobile ? '120px' : '96px'} height={mobile ? '44px' : '36px'}
            border={{ color: '#BBB' }} round="6px" pad={{ horizontal: '6px' }}>
            <Button plain a11yTitle="Decrease group size" label="−" disabled={performer.groupSize <= 2}
              onClick={() => update(index, { groupSize: performer.groupSize - 1 })}
              style={{ width: mobile ? 44 : 28, height: mobile ? 42 : 34, padding: 0, color: '#777', textAlign: 'center' }} />
            <Text size="14px">{performer.groupSize}</Text>
            <Button plain a11yTitle="Increase group size" label="+" disabled={performer.groupSize >= 50}
              onClick={() => update(index, { groupSize: performer.groupSize + 1 })}
              style={{ width: mobile ? 44 : 28, height: mobile ? 42 : 34, padding: 0, color: '#777', textAlign: 'center' }} />
          </Box> : <Text color="#bbb">—</Text>}
        </Box>
        <LineItemDelete name={formatDefaultName(performer)} onClick={() => deleteWithUndo({
          title: formatDefaultName(performer),
          hide: () => setRolePerformers((current) => current.filter((role) => role !== performer)),
          restore: () => setRolePerformers((current) => [...current.slice(0, index), performer, ...current.slice(index)]),
          commit: () => undefined,
        })} />
      </Box>;
    })}
    <Box direction="row" justify="between" align="center" wrap>
      <Button plain label="+ Add role" color="#0d48a1" onClick={() => {
        focusIndex.current = rolePerformers.length;
        setRolePerformers([...rolePerformers, { ...defaultRolePerformer }]);
      }} />
      <Text size="13px" color="#555">{rolePerformers.length} roles · {' '}
        {rolePerformers.reduce((sum, role) => sum + role.groupSize, 0)} performers</Text>
    </Box>
  </Box>;
};
